import * as Sentry from "@sentry/browser";
import { datadogRum } from "@datadog/browser-rum";
import { decodeJwtSub } from "server/kas/utils";
import { LOCALSTORAGE_USER_CACHE_PREFIX } from "constants/index";
import { safeLocalStorageSetItem } from "utils/localStorage";
import User from "../entities/User.entity";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import agent, { CreateUserRes, UserRes, UserUpdateReqBody } from "server";
import { AxiosRes, configureRequestInterceptors } from "server/config";
import { userContextDefaultValues } from "tests/defaultValues";
import { invitationClient } from "server/devplane/Invitations";
import {
  APIKeys,
  UserOnboardingStatus,
  VerificationStatus,
} from "../models/User.model";
import { usePostHog } from "posthog-js/react";
import { createDefaultAnalyticsObject } from "components/Analytics";
import Modal from "components/Modals/Modal";
import { useKas } from "./KasContext";
import { isPublicRoute } from "SecuredRoute/isPublicRoute";
import {
  getPostLoginRedirectOrDefault,
  setPostLoginRedirect,
  shouldPersistPostLoginRedirect,
} from "utils/redirects";
import { usePersonalizationCookieConsent } from "utils/oneTrustCookieConsent";
import { useQueryClient } from "@tanstack/react-query";
import { Code, ConnectError } from "@connectrpc/connect";
import { mapCurrentUser } from "server/devplane/users/mappers";
import {
  currentUserQueryOptions,
  invalidateCurrentUserQuery,
  removeCurrentUserQuery,
} from "server/devplane/users/queries";
import {
  resolveTelemetryIdentity,
  type TelemetryIdentity,
} from "utils/telemetryIdentity";

export interface UserContextType {
  me: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  getUser: (userId: string, forceRefresh?: boolean) => Promise<User | null>;
  updateUser: (updates: UserUpdateReqBody) => Promise<UserRes>;
  verifyMe: () => void;
  getMe: () => Promise<User | null>;
  refreshMe: () => Promise<User | null>;
  onboardingObject: UserOnboardingStatus;
  refreshOnboardingObject: () => Promise<void>; // Added this function
  setOnboardingObjectFunc: (updates: UserOnboardingStatus) => void;
  updateApiKeys: (apiKeys: APIKeys) => Promise<AxiosRes>;
}

export const UserContext = React.createContext<UserContextType>(
  userContextDefaultValues,
);

interface Props {
  children: React.ReactNode;
}
const UserContextProvider: React.FC<Props> = ({ children }) => {
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const hasPersonalizationCookieConsent = usePersonalizationCookieConsent();
  const [me, setMe] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [onboardingObject, setOnboardingObject] =
    useState<UserOnboardingStatus>({
      SSH: false,
      usedCLI: false,
      attemptedToConnectCloud: false,
    });
  const [showTermsModal, setShowTermsModal] = useState(false);

  const history = useHistory();
  const location = useLocation();

  const {
    isLoading: isAuthLoading,
    isUserLoggedIn,
    starfleetIdToken,
    starfleetId,
    starfleetEmail,
    user,
  } = useKas();

  // Last GetCurrentUser status lets signup distinguish a missing user from a
  // current-user request failure.
  const lastMeStatusRef = useRef<number | undefined>(undefined);

  const getCachedUser = (userId: string): User | null => {
    const user = window.localStorage.getItem(
      `${LOCALSTORAGE_USER_CACHE_PREFIX}-${userId}`,
    );

    if (!user) return null;
    return JSON.parse(user);
  };

  const setCachedUser = (user: User): void => {
    safeLocalStorageSetItem(
      `${LOCALSTORAGE_USER_CACHE_PREFIX}-${user.id}`,
      JSON.stringify(user),
    );
  };

  const getMe = async (forceRefresh = false): Promise<User | null> => {
    if (forceRefresh) {
      await invalidateCurrentUserQuery(queryClient);
    }

    try {
      const currentUser = await queryClient.fetchQuery(
        currentUserQueryOptions(),
      );
      if (!currentUser) {
        lastMeStatusRef.current = 404;
        return null;
      }

      lastMeStatusRef.current = 200;
      const user = new User(mapCurrentUser(currentUser));
      setCachedUser(user);
      return user;
    } catch (error) {
      lastMeStatusRef.current =
        ConnectError.from(error).code === Code.NotFound ? 404 : 500;
      return null;
    }
  };

  const refreshMe = async (): Promise<User | null> => {
    const user = await getMe(true);
    if (user) {
      setMe(user);
    }
    return user;
  };

  const signup = async (inviteToken: string | null): Promise<CreateUserRes> => {
    if (!starfleetIdToken) {
      return { success: false, message: "Starfleet ID token not found." };
    }
    const res = await agent.Users.registerUser(starfleetIdToken, inviteToken);
    return res;
  };

  const refreshOnboardingObject = async () => {
    const currentUser = await getMe(true);
    if (currentUser) {
      setOnboardingObject(currentUser.onboardingData);
    }
  };

  const setOnboardingObjectFunc = async (o: UserOnboardingStatus) => {
    // Optimistically update the local state
    setOnboardingObject(o);

    // Send updates to the server
    const result = await updateUser(createDefaultAnalyticsObject(me, o), false);

    // Handle potential server errors
    if (!result.success) {
      // Revert local state to previous value or handle the error differently
      console.error("Failed to update user on the server:", result.message);
      // Optionally: setOnboardingObject(previousOnboardingObject);
    }
    refreshOnboardingObject();
  };

  const updateApiKeys = async (apiKeys: APIKeys): Promise<AxiosRes> => {
    if (!me)
      return { success: false, message: "Cannot update, user not logged in." };

    // todo - we won't have all of these?
    const updates: UserUpdateReqBody = {
      username: me?.username,
      email: me?.email,
      name: me?.name,
      apiKeys: apiKeys,
    };
    const res = await agent.Users.update(me.id, updates);
    if (res.success && res.data) {
      await refreshMe();
      return { success: true };
    }
    return { success: false, message: res.message || "" };
  };

  const getUser = useCallback(
    async (
      userId: string,
      forceRefresh: boolean = false,
    ): Promise<User | null> => {
      if (!forceRefresh) {
        const user = getCachedUser(userId);
        if (user) return user;
      }
      const res = await agent.Users.getById(userId);
      if (res.success && res.data) {
        const newUser = new User(res.data);
        setCachedUser(newUser);
        return newUser;
      }
      return null;
    },
    [],
  );

  const updateUser = async (
    updates: UserUpdateReqBody,
    refreshUser: boolean = true,
  ): Promise<UserRes> => {
    if (!me)
      return { success: false, message: "Cannot update, user not logged in." };
    const res = await agent.Users.update(me.id, updates);
    if (res.success && res.data && refreshUser) {
      const newUser = new User(res.data);
      newUser.profilePhotoUrl = user?.picture;
      setMe(newUser);
    }
    return res;
  };

  const verifyMe = () => {
    if (!me) return;
    me.setVerificationStatus(VerificationStatus.Verified);
    setCachedUser(me);
  };

  const clear = () => {
    setMe(null);
    removeCurrentUserQuery(queryClient);
  };

  // Single owner of telemetry identity. PostHog receives the account ID for
  // early-access feature access; user properties require C0003 consent.
  const applyIdentity = useCallback(
    (identity: TelemetryIdentity) => {
      if (identity.kind === "user") {
        const { email, baseWorkspaceRepo, externalAuthId, id, name, username } =
          identity.user;
        const meNoSensitiveData = {
          displayName: username,
          source: "brev-workspace",
          email,
          username,
          id,
          baseWorkspaceRepo,
          externalAuthId,
          name,
        };
        if (hasPersonalizationCookieConsent) {
          posthog?.identify(id, { name, email });
        } else {
          // Without personalization cookie consent, we still needs to identify the user by their ID in Posthog
          // for feature flag evaluation.
          posthog?.identify(id);
        }
        Sentry.setUser({ ...meNoSensitiveData });
        datadogRum.setUser({ id, email });
      } else if (identity.kind === "kas") {
        const user = { id: identity.id, email: identity.email };
        Sentry.setUser(user);
        datadogRum.setUser(user);
      } else {
        Sentry.setUser(null);
        datadogRum.clearUser();
      }
    },
    [hasPersonalizationCookieConsent, posthog],
  );

  // Priority (brev user → KAS → clear) lives in resolveTelemetryIdentity.
  useEffect(() => {
    applyIdentity(
      resolveTelemetryIdentity({ me, starfleetId, starfleetEmail }),
    );
  }, [me, applyIdentity, starfleetId, starfleetEmail]);

  useEffect(() => {
    if (!me) return;

    if (me.onboardingData == null) {
      setOnboardingObject({
        SSH: false,
        usedCLI: false,
        attemptedToConnectCloud: false,
      });
    } else {
      // BANANA: should we be populating onboardingData elsewhere?
      setOnboardingObject(me?.onboardingData);
    }
    if (!me.onboardingData?.isTermsAccepted) {
      setTimeout(() => {
        setShowTermsModal(true);
      }, 1000);
    }
  }, [me]);

  // Load user and organization information
  useEffect(() => {
    const loadMeFromDBOrSignup = async () => {
      setIsLoading(true);
      // get invite token if it exists
      const inviteToken = window.localStorage.getItem(
        `${LOCALSTORAGE_USER_CACHE_PREFIX}-invite-token`,
      );
      window.localStorage.removeItem(
        `${LOCALSTORAGE_USER_CACHE_PREFIX}-invite-token`,
      );

      // get me from our DB
      const me = await getMe();
      if (me) {
        let currentMe = me;
        if (inviteToken) {
          await invitationClient.acceptInviteFromLink({ token: inviteToken });
          // Current-user data is cached indefinitely, so refresh it after the
          // invitation updates the user's organization memberships.
          currentMe = (await getMe(true)) ?? me;
        }

        currentMe.profilePhotoUrl = user?.picture;
        setMe(currentMe);
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      // If we don't exist, sign up and record whether the user was missing or
      // the current-user request failed.
      const meStatus = lastMeStatusRef.current;
      const meFailed = meStatus === undefined || meStatus >= 500;
      posthog.capture("SignupAttempt", {
        reason: meFailed ? "me_failed" : "user_not_found",
        meStatus,
        starfleetId: user?.userId,
        kasSub: decodeJwtSub(starfleetIdToken ?? null),
      });
      if (meFailed) {
        console.error(
          "[auth] signup attempted after GetCurrentUser failed — not a confirmed new user",
          { meStatus },
        );
      }
      const res = await signup(inviteToken);
      if (res.success && res.data) {
        const currentMe = (await getMe(true)) ?? new User(res.data.user);
        currentMe.profilePhotoUrl = user?.picture;
        setMe(currentMe);

        setIsAuthenticated(true);
        history.push(getPostLoginRedirectOrDefault());
        setIsLoading(false);
      } else {
        setIsAuthenticated(false);
        posthog.capture("SignUpError", {
          response: res,
          starfleetId: user?.userId,
          email: user?.email,
          kasSub: decodeJwtSub(starfleetIdToken ?? null),
        });
        history.push("/error/500", {
          title: "Signup Error",
          description: res.message,
        });
        setIsLoading(false);
      }
    };

    if (isAuthLoading) {
      setIsLoading(true);
    } else if (!isUserLoggedIn) {
      setIsAuthenticated(false);
      clear();
      // Skip auth plumbing routes so they do not overwrite a real
      // post-login destination such as an invitation callback.
      if (shouldPersistPostLoginRedirect(location.pathname)) {
        setPostLoginRedirect(window.location.pathname + window.location.search);
      }

      // Allow unauthenticated access for public routes (no redirect)
      if (isPublicRoute(location.pathname)) {
        setIsLoading(false);
        return;
      }

      // This is how we catch routes that are auth secured before redirecting to login page
      if (location.pathname.includes("/launchable/deploy/now")) {
        const searchParams = new URLSearchParams(location.search);
        //forceLogin=true is used to force the user to login before redirecting to the launchable deploy page
        if (!!searchParams.get("forceLogin")) {
          history.push("/login");
          return;
        }
        const queryString = searchParams.toString();
        const redirectPath = `/launchable/deploy${
          queryString ? `?${queryString}` : ""
        }`;
        history.push(redirectPath);
      } else {
        history.push("/logout");
      }
    } else if (isUserLoggedIn && !me) {
      setIsAuthenticated(false);
      configureRequestInterceptors(history).then(() => {
        loadMeFromDBOrSignup();
      });
    }
  }, [isAuthLoading, isUserLoggedIn]);

  const providerValues = {
    me,
    isAuthenticated,
    isLoading,
    getUser,
    updateUser,
    verifyMe,
    getMe,
    refreshMe,
    refreshOnboardingObject,
    onboardingObject,
    setOnboardingObjectFunc,
    updateApiKeys,
  };

  return (
    <>
      <Modal
        setOpen={setShowTermsModal}
        isOpen={showTermsModal}
        onClose={() => {
          history.push("/logout");
        }}
        disableClickOutside={true}
        onSuccess={() => {
          setOnboardingObjectFunc({
            isTermsAccepted: true,
          });
        }}
        title={"Terms of Service"}
        body={
          <>
            <div className="flex flex-row items-center">
              <p className="text-sm text-gray-500 dark:text-secondary mr-1">
                I understand and agree to to the
              </p>
              <p
                onClick={() => {
                  window.open(
                    "https://www.nvidia.com/en-us/agreements/cloud-services/service-specific-terms-for-brev/",
                    "_blank",
                  );
                }}
                className="text-sm text-gray-500 dark:text-secondary underline mr-1 cursor-pointer"
              >
                Terms of Service
              </p>
              <p className="text-sm text-gray-500 dark:text-secondary mr-1">
                and
              </p>
              <p
                onClick={() => {
                  window.open(
                    "https://www.nvidia.com/en-us/about-nvidia/privacy-policy/",
                    "_blank",
                  );
                }}
                className="text-sm text-gray-500 dark:text-secondary underline cursor-pointer"
              >
                Privacy Policy
              </p>
              <p className="text-sm text-gray-500 dark:text-secondary mr-1">
                .
              </p>
            </div>
          </>
        }
        confirmLabel="Agree and Continue"
      />
      <UserContext.Provider value={providerValues}>
        {children}
      </UserContext.Provider>
    </>
  );
};

export default UserContextProvider;
